<template>
  <div class="page">
    <div class="page-main">
      <div class="page-header">
        <h1 class="page-title">{{ $t("account.labels.editProfile") }}</h1>
      </div>
      <div class="page-content">
        <div class="panel">
          <div class="panel-body panel-form">
            <loading-indicator v-if="isLoading"></loading-indicator>
            <form v-else v-on:submit.prevent="onSubmit" autocomplete="off">
              <div class="row">
                <div class="col-xs-12 col-md-6">
                  <alert v-show="showAlert"
                    alertClass="m-b-25 alert-dismissible"
                    v-bind:type="`${ (successResponse) ? 'success' : 'danger' }`"
                    v-bind:iconClass="`${ (successResponse) ? 'md-check' : 'md-close' }`">
                    <span v-if="successResponse">{{successResponse}}</span>
                    <span v-else-if="errorResponse">{{errorResponse}}</span>
                  </alert>
                  <div class="form-section">
                    <div class="form-group form-avatar">
                      <span class="avatar avatar-lg">
                        <img v-if="currentUser" class="img-fluid" v-bind:src="currentUser.display_image.sizes.default.url" alt="image-profile">
                        <img v-else class="img-fluid" src="/static/images/user.png" alt="image-profile">
                        <span>{{ $t("account.profile.labels.changeDisplayImage") }} <a href="http://www.gravatar.com" target="_blank">Gravatar.com</a>
                        </span>
                      </span>
                    </div>
                    <form-group v-bind:error="formErrors.first('name')">
                      <label for="name">{{ $t("account.labels.displayName") }}</label>
                      <input type="text" class="form-control input-lg" :placeholder="$t('account.labels.displayName')" v-model="user.name">
                    </form-group>
                    <form-group>
                      <label for="email">{{ $t("account.labels.email") }}</label>
                      <input type="email" class="form-control input-lg" :placeholder="$t(
                      'account.labels.displayName')" v-model="user.email" autocomplete="false" :disabled="true">
                    </form-group>
                    <form-group>
                      <label for="password">{{ $t("account.labels.password") }}</label>
                      <router-link v-bind:to="`/${$route.params.namespace}/self/password`" class="btn btn-icon btn-block btn-link text-xs-left p-l-0">
                        {{ $t("member.action.changePassword") }}
                      </router-link>
                    </form-group>
                    <form-group v-bind:error="formErrors.first('lang')">
                      <label for="password">{{ $t("account.profile.labels.chooseLanguage") }}</label>
                      <div class="radio-custom radio-primary">
                        <input type="radio" value="th" v-model="user.lang">
                        <label for="inputRadiosChecked">{{ $t("account.lang.th") }}</label>
                      </div>
                      <div class="radio-custom radio-primary">
                        <input type="radio" value="en" v-model="user.lang">
                        <label for="inputRadiosChecked">{{ $t("account.lang.en") }}</label>
                      </div>
                    </form-group>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-4">
                  <button type="submit" class="btn btn-success" :disabled="isSubmiting" v-bind:class="{ 'btn-default': isSubmiting }">
                    <div class="loader vertical-align-middle loader-circle loader-form" v-if="isSubmiting"></div> {{ $t("account.action.submit") }}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import axios from 'axios';
import { Validator } from 'vee-validate';
import { mapGetters } from 'vuex';
import Alert from 'components/Alert';
import FormGroup from 'components/forms/FormGroup';
import LoadingIndicator from 'components/LoadingIndicator';
import StorageManager from 'src/managers/StorageManager';

export default {
  components: {
    FormGroup,
    Alert,
    LoadingIndicator,
  },
  watch: {
    'user.name': function() {
      if (this.formErrors.has('name')) {
        this.formErrors.remove('name');
      }
    },
    'user.lang': function() {
      if (this.formErrors.has('lang')) {
        this.formErrors.remove('lang');
      }
    },
  },
  computed: {
    ...mapGetters([
      'currentUser',
    ]),
    showEmail: function () {
      if (this.currentUser.team.is_superadmin) {
        return true;
      } else {
        return false;
      }
    },
  },
  data: function () {
    return {
      isSubmiting: false,
      isLoading: false,
      showAlert: false,
      validator: null,
      formErrors: null,
      errorResponse: null,
      successResponse: null,
      user: null,
      storage: null,
    };
  },
  created: function() {
    this.isUserLoading = true;
    this.user = JSON.parse(JSON.stringify(this.currentUser));

    this.storage = new StorageManager();

    this.validator = new Validator();
    this.validator.attach('name', 'required', { prettyName: 'display name' });
    this.validator.attach('lang', 'required', { prettyName: 'language' });

    this.$set(this, 'formErrors', this.validator.errorBag);
  },
  methods: {
    onSubmit: function () {
      this.showAlert = false;
      this.validator.validateAll({
        name: this.user.name,
        lang: this.user.lang,
      })
      .then((success) => {
        if (!success) {
          return;
        }

        this.isSubmiting = true;

        axios.put(`/api/user`, {
          name: this.user.name,
          lang: this.user.lang,
        })
        .then((response) => {
          const namespace = this.currentUser.team.namespace;

          // check lang
          this.storage.getLang(namespace).then((lang) => {
            if (response.data.lang !== lang) {
              this.storage.setLang(response.data.lang, namespace);
              this.$i18n.locale = response.data.lang;
              Validator.setLocale(response.data.lang);
              moment.locale(response.data.lang);

              this.$store.dispatch('updateCurrentUserLang', response.data.lang);
            }
          });

          this.isSubmiting = false;
          this.successResponse = this.$t('team.response.success');
          this.errorResponse = null;
          this.showAlert = true;
        })
        .catch((error) => {
          this.isSubmiting = false;

          if (error.response.status === 422) {
            this.errorResponse = this.$t('team.response.failed');
            this.formErrors.addAll(error.response.data);
          } else {
            this.errorResponse = this.$t('team.response.defaultFailed');
          }
          this.showAlert = true;
        });
      })
      .catch(() => {
      });
    },
  },
};
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// Profile.vue?3fd64fa2